import { router, usePage } from "@inertiajs/react"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import { Field } from "@/components/steel"
import { Input } from "@/components/ui/input"
import { formatCurrency } from "@/lib/utils"
import type { PayrollRun, ProjectShowProps } from "@/pages/Projects/tabs/types"
import type { SharedProps } from "@/types"

const EMPTY_RUN = { amount: "", periodEnd: "", periodStart: "" }

function formatPeriod(run: PayrollRun): string {
  return `${run.periodStart} – ${run.periodEnd}`
}

export function LaborCostPanel({ projectId }: { projectId: string }) {
  const { flash, production } = usePage<ProjectShowProps & SharedProps>().props
  const laborRate = production.laborRate
  const payrollRuns = production.payrollRuns ?? []
  const [rate, setRate] = useState(laborRate != null ? String(laborRate) : "")
  const [run, setRun] = useState(EMPTY_RUN)

  const saveRate = () => router.patch(`/projects/${projectId}`, { laborRate: rate }, { preserveScroll: true })

  const addRun = () => {
    if (!run.amount || !run.periodEnd || !run.periodStart) return
    router.post(`/projects/${projectId}/payroll_runs`, run, {
      preserveScroll: true,
      onSuccess: () => setRun(EMPTY_RUN),
    })
  }

  return (
    <div className="mt-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-title font-semibold">Labor cost</h2>
          <p className="mt-0.5 text-meta text-muted-foreground">
            Section hours cost this rate per hour, except where a payroll run covers their date.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Field label="Labor rate ($/hr)" className="w-[150px]">
            <Input
              className="num"
              type="number"
              min="0"
              placeholder="—"
              value={rate}
              onBlur={saveRate}
              onChange={(e) => setRate(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveRate()}
            />
          </Field>
        </div>
      </div>

      {flash.alert ? (
        <p className="mb-3 border border-destructive px-3 py-2 text-label text-destructive">{flash.alert}</p>
      ) : null}

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Field label="Period start" className="w-[160px]">
          <Input
            type="date"
            value={run.periodStart}
            onChange={(e) => setRun((r) => ({ ...r, periodStart: e.target.value }))}
          />
        </Field>
        <Field label="Period end" className="w-[160px]">
          <Input
            type="date"
            value={run.periodEnd}
            onChange={(e) => setRun((r) => ({ ...r, periodEnd: e.target.value }))}
          />
        </Field>
        <Field label="Amount" className="w-[160px]">
          <Input
            className="num"
            type="number"
            min="0"
            value={run.amount}
            onChange={(e) => setRun((r) => ({ ...r, amount: e.target.value }))}
          />
        </Field>
        <Button size="sm" onClick={addRun}>
          <Plus />
          Record payroll
        </Button>
      </div>

      {payrollRuns.length === 0 ? (
        <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
          No payroll recorded yet. Hours in every period cost the labor rate.
        </p>
      ) : (
        <DataTable>
          <Thead>
            <Th>Period</Th>
            <Th align="right">Amount</Th>
            <Th />
          </Thead>
          <tbody>
            {payrollRuns.map((payrollRun) => (
              <Tr key={payrollRun.id}>
                <Td num muted>
                  {formatPeriod(payrollRun)}
                </Td>
                <Td align="right" num strong>
                  {formatCurrency(payrollRun.amount)}
                </Td>
                <Td align="right">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => router.delete(`/payroll_runs/${payrollRun.id}`, { preserveScroll: true })}
                  >
                    <Trash2 />
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  )
}
